import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { isSmsConsentRefusal } from 'app/lib/workflows/smsConsent'
import type { RemoteResult } from 'app/lib/workflows/workflowApi'
import { CONFIRM_MARKETING_CONSENT_MUTATION, WORKSPACE_CONSENT_QUERY } from './graphql'
import { SmsConsentModal } from './SmsConsentModal'
import { SMS_CONSENT_TEXT, SMS_CONSENT_VERSION } from './smsConsentText'

type EnsureOptions = {
  /** Where focus goes on close when the control that started this is gone (the preflight's button
   *  unmounts before the modal opens). Otherwise focus returns to whatever had it. */
  returnFocus?: () => HTMLElement | null
}

type Prompt = {
  workspaceName: string
  settle: (agreed: boolean) => void
}

/**
 * The SMS consent confirmation gate, client side. `ensure` resolves true when the action may go
 * ahead: the workspace already confirmed, or just did. It resolves false when the user backed out.
 *
 * The server is the rule (it refuses the activation); this exists so the question is asked BEFORE
 * anything is saved, and so an agree resumes the original action without a second click.
 *
 * Backing out collapses the question instead of dropping it: `collapsed` stays true until the
 * workspace confirms, and the caller shows SmsConsentBar for it.
 */
export function useSmsConsentGate(workspaceId: string | undefined): {
  ensure: (options?: EnsureOptions) => Promise<boolean>
  runGated: <T>(action: () => Promise<RemoteResult<T>>, options?: EnsureOptions) => Promise<RemoteResult<T>>
  modal: ReactNode
  collapsed: boolean
} {
  const [prompt, setPrompt] = useState<Prompt | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string>()
  const [dismissed, setDismissed] = useState(false)
  const returnFocus = useRef<EnsureOptions[`returnFocus`]>(undefined)
  // A builder that unmounts mid-question must not leave its save awaiting forever.
  const openPrompt = useRef<Prompt | null>(null)
  useEffect(() => {
    openPrompt.current = prompt
  }, [prompt])
  useEffect(() => () => openPrompt.current?.settle(false), [])

  const ensure = useCallback(
    async (options: EnsureOptions = {}) => {
      if (!workspaceId) return true
      // The server's answer, not the document cache: another tab may have confirmed since.
      const { data } = await gqlQuery(WORKSPACE_CONSENT_QUERY, { id: workspaceId }, { requestPolicy: `network-only` })
      const workspace = data?.workspace
      // Couldn't ask (offline, an older API): go ahead. The server still refuses what it must, and
      // runGated brings the refusal back here.
      if (!workspace || workspace.marketingConsentConfirmedDate) {
        // Confirmed elsewhere (another tab, a colleague) since this one was closed.
        setDismissed(false)
        return true
      }
      returnFocus.current = options.returnFocus
      setError(undefined)
      return new Promise<boolean>((settle) => setPrompt({ workspaceName: workspace.name, settle }))
    },
    [workspaceId],
  )

  const runGated = useCallback(
    async <T,>(action: () => Promise<RemoteResult<T>>, options?: EnsureOptions) => {
      const first = await action()
      // The pre-check can be wrong (a second tab, a stale read). The server's refusal opens the
      // same modal, and an agree retries exactly once.
      if (!isSmsConsentRefusal(first.error)) return first
      return (await ensure(options)) ? action() : first
    },
    [ensure],
  )

  const agree = async (current: Prompt) => {
    setSubmitting(true)
    setError(undefined)
    // The error stays in the modal, where the user can try again; no toast on top of it.
    const result = await gqlMutation(CONFIRM_MARKETING_CONSENT_MUTATION, {}, { suppressErrorToast: true })
    setSubmitting(false)
    if (result.error || !result.data) {
      setError(result.error?.message.replace(/^\[GraphQL\]\s*/, ``) ?? `The server returned nothing. Try again.`)
      return
    }
    setPrompt(null)
    setDismissed(false)
    current.settle(true)
  }

  const cancel = (current: Prompt) => {
    setPrompt(null)
    setDismissed(true)
    current.settle(false)
  }

  const modal = prompt && (
    <SmsConsentModal
      key={SMS_CONSENT_VERSION}
      open
      workspaceName={prompt.workspaceName}
      text={SMS_CONSENT_TEXT}
      submitting={submitting}
      error={error}
      onAgree={() => agree(prompt)}
      onCancel={() => cancel(prompt)}
      onCloseAutoFocus={(event) => {
        const target = returnFocus.current?.()
        if (!target?.isConnected) return
        event.preventDefault()
        target.focus()
      }}
    />
  )

  return { ensure, runGated, modal, collapsed: dismissed && !prompt }
}
